import type { CleanCopiesAnswer } from 'fym-shared/src/music24/watermarkUpsell';
import { useGetCleanCopiesQuery } from '../../../redux/api/fymAPI';
import { useAccountId, useUserToken } from '../../../redux/slices/appInfo/hooks';
import { useRealtimePollInterval } from '../../realtime/useRealtimePollInterval';

/** The poll of a running clean-copies run while the socket is down. */
export const CLEAN_COPIES_POLL_MS = 15 * 1000;

export interface CleanCopiesState {
  /** The run of the workspace; null when the licence has none (and for the demo artist). */
  cleanCopies: CleanCopiesAnswer | null;
  isLoading: boolean;
}

/**
 * The clean-copies run of the active artist's workspace (`GET /accounts/:accountId/clean-copies`,
 * design: trial-upsell b5). `kit.asset.updated` and `reel.updated` read it again
 * (`realtimeEventTags`), at most one time in 3 seconds (`THROTTLED_REALTIME_TAGS`), so it polls only
 * while a run is `running` and the socket is down.
 */
export function useCleanCopies(options?: {
  skip?: boolean;
  /**
   * Reads again each this many milliseconds while the licence has no run, with the socket up or
   * down. The payment return page uses it: the run row is made by a job after the payment, and no
   * event says that it exists. 0 or absent: no such poll.
   */
  pollWhileNoRunMs?: number;
}): CleanCopiesState {
  const accountId = useAccountId();
  const token = useUserToken();
  const skip = options?.skip === true || accountId === null || token === null || token === '';
  const args = { accountId: accountId ?? 0 };
  const { data, isLoading } = useGetCleanCopiesQuery(args, { skip });
  const cleanCopies = data?.cleanCopies ?? null;
  const runningPoll = useRealtimePollInterval(cleanCopies?.status === 'running' ? CLEAN_COPIES_POLL_MS : 0);
  const noRunPoll = cleanCopies === null ? (options?.pollWhileNoRunMs ?? 0) : 0;
  const pollingInterval = noRunPoll > 0 ? noRunPoll : runningPoll;
  // A second subscription to the same cache entry sets the poll; RTK Query takes the shortest interval.
  useGetCleanCopiesQuery(args, { skip: skip || pollingInterval === 0, pollingInterval });
  return { cleanCopies: skip ? null : cleanCopies, isLoading };
}
